// src/pages/distribuicao/FichasTransporte.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";
import Draggable from "react-draggable";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// UI base JFB
import PageHeader from "pages/components/PageHeader";
import { useSpeechRecognition } from "pages/components/useSpeechRecognition";
import DialogFichaTransporteFicheiros from "pages/distribuicao/DialogFichaTransporteFicheiros";
import DialogFichaTransporteDetalhe from "pages/distribuicao/DialogFichaTransporteDetalhe";
import DialogFichaTransporteForm from "pages/distribuicao/DialogFichaTransporteForm";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Card,
  CircularProgress,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";

// =====================================================
// react-table column filter (padrão underline)
// =====================================================
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// =====================================================
// Helpers
// =====================================================
function nullIfEmpty(s) {
  const v = (s ?? "").toString().trim();
  return v === "" ? null : v;
}

function norm01(v) {
  return v === 1 ? 1 : 0;
}

function normTri(v) {
  const n = Number(v);
  return n === 0 || n === 1 || n === 2 ? n : 0;
}

function triLabel(v) {
  const n = Number(v);
  if (n === 0) return "PENDENTE";
  if (n === 1) return "CORRETA";
  if (n === 2) return "INCORRETA";
  return "PENDENTE";
}

function toInputDate(d) {
  const pad = (x) => x.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatRefTransporteOnBlur(v) {
  const raw = (v ?? "").toString().trim();
  if (raw === "") return "";

  if (/^\d+$/.test(raw) && raw.length <= 5) return `30000${raw}`;
  return raw;
}

function formatMatriculaOnBlur(v) {
  const raw = (v ?? "").toString().trim().toUpperCase();
  if (raw === "") return "";

  const clean = raw.replace(/[^A-Z0-9]/g, "");
  if (clean.length === 6) {
    return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4, 6)}`;
  }
  return raw;
}

function matriculaCleanLength(v) {
  const raw = (v ?? "").toString().trim().toUpperCase();
  const clean = raw.replace(/[^A-Z0-9]/g, "");
  return clean.length;
}

function isBusinessDay(d) {
  const day = d.getDay(); // 0 dom, 6 sáb
  return day !== 0 && day !== 6;
}

function minDateNBusinessDaysBack(n) {
  let d = new Date();
  d.setHours(0, 0, 0, 0);
  let count = 0;

  while (count < n) {
    d.setDate(d.getDate() - 1);
    if (isBusinessDay(d)) count++;
  }
  return d;
}

function podeAlterarAuxilio(dataFichaStr, isAdminLike, isArmazem) {
  if (isAdminLike) return true;
  if (!isArmazem) return false;

  const df = new Date(dataFichaStr);
  df.setHours(0, 0, 0, 0);

  const min = minDateNBusinessDaysBack(2);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return df >= min && df <= today;
}

function createEmptyForm() {
  return {
    id: 0,
    dataficha: toInputDate(new Date()),
    reftransporte: "",
    motorista: "",
    ajudante: "",
    matricula: "",
    observacoes: "",

    // estado ficha (0 aberta, 1 encerrada)
    estado: 0,

    // mapa de carga (0 pendente, 1 correta, 2 incorreta)
    conf_mapa_carga: 0,
    conf_mapa_carga_desc: "",

    // campos “read-only” para mostrar no detalhe / futura UI
    criacao_nome: "",
    conf_mapa_carga_nome: "",
    dif_mercadoria_nome: "",
    quebras_danificado_nome: "",
    dif_vasilhame_nome: "",
    encerramento_nome: "",
    reabertura_nome: "",

    auxilio_mot_arm1: 2,
    auxilio_mot_arm2: 2,
    auxilio_ajud_arm1: 2,
    auxilio_ajud_arm2: 2,

    dif_mercadoria: 0,
    dif_mercadoria_desc: "",
    quebras_danificado: 0,
    quebras_danificado_desc: "",
    dif_vasilhame: 0,
    dif_vasilhame_desc: "",
    limpeza_viatura: 2,
  };
}

// =====================================================
// Component
// =====================================================
function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-transportes-sem-ficha-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

export default function FichasTransporte() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const idutilizador = userData?.id || 0;

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const isArmazem = temFuncao("Armazém");
  const isDistribuicao = temFuncao("Distribuição");
  const podeCriar = isAdminLike || isArmazem;
  const podeEditarLimitado = isDistribuicao && !isAdminLike && !isArmazem;
  const podeAbrirDialogEdicao = podeCriar || podeEditarLimitado;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // filtros de pesquisa (defaults últimos 30 dias)
  const [filtro, setFiltro] = useState(() => {
    const hoje = new Date();
    const ini = new Date();
    ini.setDate(hoje.getDate() - 30);

    return {
      dataIni: toInputDate(ini),
      dataFim: toInputDate(hoje),
      reftransporte: "",
      matricula: "",
      pessoa: "",
    };
  });

  const filtroRef = useRef(filtro);

  useEffect(() => {
    filtroRef.current = filtro;
  }, [filtro]);

  // combos (pessoas)
  const [pessoas, setPessoas] = useState([]);

  const [matriculas, setMatriculas] = useState([]);

  // dialog
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [refTransporteDuplicada, setRefTransporteDuplicada] = useState(false);
  const [idFichaExistente, setIdFichaExistente] = useState(0);
  const [estadoFichaExistente, setEstadoFichaExistente] = useState(0);

  const [openInfo, setOpenInfo] = useState(false);
  const [infoRow, setInfoRow] = useState(null);
  const [openFicheiros, setOpenFicheiros] = useState(false);
  const [fichaFicheiros, setFichaFicheiros] = useState(null);
  const [openConfirmarEncerramento, setOpenConfirmarEncerramento] = useState(false);

  const [openSemFicha, setOpenSemFicha] = useState(false);
  const [loadingSemFicha, setLoadingSemFicha] = useState(false);
  const [rowsSemFicha, setRowsSemFicha] = useState([]);
  const [prefillSemFicha, setPrefillSemFicha] = useState(false);

  const handleOpenInfo = (row) => {
    setInfoRow(row);
    setOpenInfo(true);
  };

  const handleCloseInfo = () => {
    setOpenInfo(false);
    setInfoRow(null);
  };

  const handleOpenFicheiros = (row) => {
    setFichaFicheiros(row);
    setOpenFicheiros(true);
  };

  const handleCloseFicheiros = () => {
    setOpenFicheiros(false);
    setFichaFicheiros(null);
    pesquisar(filtroRef.current); // força recarregar a tabela
  };

  const handleOpenSemFicha = async () => {
    if (!idutilizador) return;

    setOpenSemFicha(true);
    setLoadingSemFicha(true);
    setRowsSemFicha([]);

    const pars = {
      dataIni: filtro.dataIni,
      dataFim: filtro.dataFim,
      reftransporte: nullIfEmpty(filtro.reftransporte),
      matricula: nullIfEmpty(filtro.matricula),
      pessoa: nullIfEmpty(filtro.pessoa),
      idutilizador,
    };

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelTransportesSemFicha", pars)
    );

    if (err) {
      showAlert(err, "error");
      setLoadingSemFicha(false);
      return;
    }

    setRowsSemFicha(Array.isArray(dados) ? dados : []);
    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    setLoadingSemFicha(false);
  };

  const handleCloseSemFicha = () => {
    setOpenSemFicha(false);
    setRowsSemFicha([]);
  };

  const handleCriarFichaDeTransporte = async (row) => {
    handleCloseSemFicha();
    handleOpenDialogNovo();
    setPrefillSemFicha(true);

    const dataFicha = formatDateOnly(row?.dataficha) || toInputDate(new Date());
    const reftransporte = row?.reftransporte || "";
    const matricula = row?.matricula || "";
    const motorista = row?.motorista || "";
    const ajudante = row?.ajudante || "";

    setForm((s) => ({
      ...s,
      dataficha: dataFicha,
      reftransporte,
      matricula,
      motorista,
      ajudante,
      auxilio_mot_arm1: motorista ? s.auxilio_mot_arm1 : 2,
      auxilio_mot_arm2: motorista ? s.auxilio_mot_arm2 : 2,
      auxilio_ajud_arm1: ajudante ? s.auxilio_ajud_arm1 : 2,
      auxilio_ajud_arm2: ajudante ? s.auxilio_ajud_arm2 : 2,
    }));
  };

  const [form, setForm] = useState(() => createEmptyForm());

  const [campoVozAtivo, setCampoVozAtivo] = useState("");

  const {
    iniciar: iniciarVozObservacoes,
    parar: pararVozObservacoes,
    reconhecendo: reconhecendoVozObservacoes,
    suporteVoz,
    erro: erroVoz,
  } = useSpeechRecognition((texto) => {
    setForm((s) => ({
      ...s,
      observacoes: s.observacoes ? `${s.observacoes}\n${texto}` : texto,
    }));
  });

  const {
    iniciar: iniciarVozConfMapaCargaDesc,
    parar: pararVozConfMapaCargaDesc,
    reconhecendo: reconhecendoVozConfMapaCargaDesc,
    erro: erroVozConfMapaCargaDesc,
  } = useSpeechRecognition((texto) => {
    setForm((s) => ({
      ...s,
      conf_mapa_carga_desc: s.conf_mapa_carga_desc ? `${s.conf_mapa_carga_desc}\n${texto}` : texto,
    }));
  });

  const {
    iniciar: iniciarVozDifMercadoriaDesc,
    parar: pararVozDifMercadoriaDesc,
    reconhecendo: reconhecendoVozDifMercadoriaDesc,
    erro: erroVozDifMercadoriaDesc,
  } = useSpeechRecognition((texto) => {
    setForm((s) => ({
      ...s,
      dif_mercadoria_desc: s.dif_mercadoria_desc ? `${s.dif_mercadoria_desc}\n${texto}` : texto,
    }));
  });

  const {
    iniciar: iniciarVozQuebrasDanificadoDesc,
    parar: pararVozQuebrasDanificadoDesc,
    reconhecendo: reconhecendoVozQuebrasDanificadoDesc,
    erro: erroVozQuebrasDanificadoDesc,
  } = useSpeechRecognition((texto) => {
    setForm((s) => ({
      ...s,
      quebras_danificado_desc: s.quebras_danificado_desc
        ? `${s.quebras_danificado_desc}\n${texto}`
        : texto,
    }));
  });

  const {
    iniciar: iniciarVozDifVasilhameDesc,
    parar: pararVozDifVasilhameDesc,
    reconhecendo: reconhecendoVozDifVasilhameDesc,
    erro: erroVozDifVasilhameDesc,
  } = useSpeechRecognition((texto) => {
    setForm((s) => ({
      ...s,
      dif_vasilhame_desc: s.dif_vasilhame_desc ? `${s.dif_vasilhame_desc}\n${texto}` : texto,
    }));
  });

  useEffect(() => {
    const erros = [
      erroVoz,
      erroVozConfMapaCargaDesc,
      erroVozDifMercadoriaDesc,
      erroVozQuebrasDanificadoDesc,
      erroVozDifVasilhameDesc,
    ].filter((e) => e && e !== "no-speech");

    if (erros.length > 0) {
      showAlert(`Falha no reconhecimento de voz: ${erros[0]}`, "warning");
    }
  }, [
    erroVoz,
    erroVozConfMapaCargaDesc,
    erroVozDifMercadoriaDesc,
    erroVozQuebrasDanificadoDesc,
    erroVozDifVasilhameDesc,
    showAlert,
  ]);

  useEffect(() => {
    const existeGravacaoAtiva =
      reconhecendoVozObservacoes ||
      reconhecendoVozConfMapaCargaDesc ||
      reconhecendoVozDifMercadoriaDesc ||
      reconhecendoVozQuebrasDanificadoDesc ||
      reconhecendoVozDifVasilhameDesc;

    if (!existeGravacaoAtiva && campoVozAtivo) {
      setCampoVozAtivo("");
    }
  }, [
    reconhecendoVozObservacoes,
    reconhecendoVozConfMapaCargaDesc,
    reconhecendoVozDifMercadoriaDesc,
    reconhecendoVozQuebrasDanificadoDesc,
    reconhecendoVozDifVasilhameDesc,
    campoVozAtivo,
  ]);

  const botaoVozDesativado = (nomeCampo) =>
    !suporteVoz || (!!campoVozAtivo && campoVozAtivo !== nomeCampo);

  // =====================================================
  // Columns (com filtros underline)
  // =====================================================
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        disableSortBy: true,
        width: 112,
        minWidth: 96,
        maxWidth: 128,
        Cell: ({ row }) => {
          const fichaEncerrada = row.original?.estado === 1;

          // só fichas abertas podem ser editadas no dialog
          const podeEditarEstaFicha = !fichaEncerrada && podeAbrirDialogEdicao;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              {podeAbrirDialogEdicao && (
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(row.original)}
                  title={fichaEncerrada ? "Ficha encerrada" : "Editar (ficha aberta)"}
                  disabled={!podeEditarEstaFicha}
                >
                  <EditIcon
                    fontSize="small"
                    style={{ color: podeEditarEstaFicha ? "#d32f2f" : "#bdbdbd" }}
                  />
                </IconButton>
              )}

              <IconButton
                size="small"
                onClick={() => handleToggleEstadoLinha(row.original)}
                title={
                  fichaEncerrada
                    ? isAdminLike
                      ? "Reabrir ficha"
                      : "Ficha encerrada"
                    : "Encerrar ficha"
                }
                disabled={fichaEncerrada && !isAdminLike}
              >
                {fichaEncerrada ? (
                  <LockIcon fontSize="small" style={{ color: "black" }} />
                ) : (
                  <LockOpenIcon fontSize="small" style={{ color: "black" }} />
                )}
              </IconButton>

              <IconButton
                size="small"
                onClick={() => handleOpenFicheiros(row.original)}
                title={row.original?.temanexos === 1 ? "Anexos existentes" : "Sem anexos"}
              >
                <AttachFileIcon
                  color={row.original?.temanexos === 1 ? "info" : "disabled"}
                  fontSize="small"
                />
              </IconButton>

              <IconButton size="small" onClick={() => handleOpenInfo(row.original)} title="Info">
                <InfoOutlinedIcon color="info" fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },

      {
        Header: "Data",
        accessor: "dataficha",
        Filter: TextFilter,
        Cell: ({ value }) => formatDateOnly(value),
      },

      {
        Header: "Ref. Transp.",
        accessor: "reftransporte",
        Filter: TextFilter,
        Cell: ({ value }) => (
          <span
            onClick={() => abrirTransportesRef(value)}
            title="Ver transportes desta referência"
            style={{
              color: "#1a73e8",
              textDecoration: "underline",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {value}
          </span>
        ),
      },

      { Header: "Distribuidor 1", accessor: "motorista", Filter: TextFilter },
      { Header: "Distribuidor 2", accessor: "ajudante", Filter: TextFilter },
      { Header: "Matrícula", accessor: "matricula", Filter: TextFilter },

      {
        Header: "Aux. Distr.1 (Arm.1)",
        accessor: "auxilio_mot_arm1",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },

      {
        Header: "Aux. Distr.2 (Arm.1)",
        accessor: "auxilio_ajud_arm1",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },

      {
        Header: "Aux. Distr.1 (Arm.2)",
        accessor: "auxilio_mot_arm2",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },
      {
        Header: "Aux. Distr.2 (Arm.2)",
        accessor: "auxilio_ajud_arm2",
        Filter: TextFilter,
        Cell: ({ value }) => (value === 1 ? "Sim" : "Não"),
      },
      {
        Header: "Mapa Carga",
        accessor: "conf_mapa_carga",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Dif. Mercad.",
        accessor: "dif_mercadoria",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Quebras/Danif.",
        accessor: "quebras_danificado",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Dif. Vasil.",
        accessor: "dif_vasilhame",
        Filter: TextFilter,
        Cell: ({ value }) => triLabel(value),
      },
      {
        Header: "Limpeza Viat.",
        accessor: "limpeza_viatura",
        Filter: TextFilter,
        Cell: ({ value }) =>
          Number(value) === 1 ? "Sim" : Number(value) === 0 ? "Não" : "Indefinido",
      },
    ],
    [isAdminLike, podeCriar, podeAbrirDialogEdicao]
  );

  const data = useMemo(() => rows, [rows]);

  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: TextFilter },
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = tableInstance;

  // =====================================================
  // Load combos (pessoas)
  // =====================================================
  const carregarPessoas = async () => {
    if (!idutilizador) return;

    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelTransportesCombos", { idutilizador })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    const lista = dados?.pessoas || [];
    setPessoas(Array.isArray(lista) ? lista : []);
  };

  const carregarEquipamentos = async () => {
    const [dados, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentos", { mostrar_inativos: 0 })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    const lista = Array.isArray(dados) ? dados : [];

    const listaComMatricula = lista.filter((e) => (e?.matricula || "").toString().trim() !== "");

    const matriculasUnicas = [...new Set(listaComMatricula.map((e) => e.matricula.trim()))].sort(
      (a, b) => a.localeCompare(b, "pt")
    );

    setMatriculas(matriculasUnicas);
  };

  // =====================================================
  // Pesquisa
  // =====================================================
  const pesquisar = async (filtroAtual = filtroRef.current) => {
    if (!idutilizador) return;

    setLoading(true);

    const pars = {
      dataIni: filtroAtual.dataIni,
      dataFim: filtroAtual.dataFim,
      reftransporte: nullIfEmpty(filtroAtual.reftransporte),
      matricula: nullIfEmpty(filtroAtual.matricula),
      pessoa: nullIfEmpty(filtroAtual.pessoa),
      idutilizador,
    };

    const [dados, err, meta] = await handleApi(api.post("Distribuicao/SelFichasTransporte", pars));

    if (err) {
      showAlert(err, "error");
      setLoading(false);
      return;
    }

    setRows(Array.isArray(dados) ? dados : []);
    //if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    setLoading(false);
  };

  useEffect(() => {
    if (idutilizador > 0) {
      (async () => {
        await carregarPessoas();
        await carregarEquipamentos();
        await pesquisar(filtroRef.current);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idutilizador]);

  // =====================================================
  // Dialog open/close
  // =====================================================

  const carregarUltimaEquipaPorMatricula = async (matricula, reftransporte) => {
    const mat = formatMatriculaOnBlur(matricula || "");
    const ref = formatRefTransporteOnBlur(reftransporte || "");

    if (!mat && !ref) return;

    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelUltimaEquipaPorMatricula", {
        matricula: mat || null,
        reftransporte: ref || null,
      })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    const matriculaResposta = formatMatriculaOnBlur((dados?.matricula || "").toString().trim());
    const motorista = (dados?.motorista || "").toString().trim();
    const ajudante = (dados?.ajudante || "").toString().trim();

    setForm((s) => ({
      ...s,
      matricula: mat || matriculaResposta || s.matricula,
      motorista,
      ajudante,
      auxilio_mot_arm1: motorista ? s.auxilio_mot_arm1 : 2,
      auxilio_mot_arm2: motorista ? s.auxilio_mot_arm2 : 2,
      auxilio_ajud_arm1: ajudante ? s.auxilio_ajud_arm1 : 2,
      auxilio_ajud_arm2: ajudante ? s.auxilio_ajud_arm2 : 2,
    }));
  };

  const verificarFichaTransportePorRef = async (reftransporte, idAtual = 0) => {
    const ref = formatRefTransporteOnBlur(reftransporte || "");
    if (!ref) {
      setRefTransporteDuplicada(false);
      setIdFichaExistente(0);
      setEstadoFichaExistente(0);
      return { existe: false };
    }

    const [dados, err] = await handleApi(
      api.post("Distribuicao/VerificarFichaTransportePorRef", {
        reftransporte: ref,
        id: Number(idAtual || 0),
      })
    );

    if (err) {
      showAlert(err, "error");
      setRefTransporteDuplicada(false);
      setIdFichaExistente(0);
      setEstadoFichaExistente(0);
      return null;
    }

    const existe = dados?.existe === true;
    const idExistente = existe ? Number(dados?.id || 0) : 0;

    setRefTransporteDuplicada(existe);
    setIdFichaExistente(idExistente);

    const rowExistente = rows.find((r) => Number(r.id) === idExistente);
    setEstadoFichaExistente(existe ? Number(rowExistente?.estado ?? 0) : 0);

    return dados || { existe: false };
  };

  const resetFormNovo = () => {
    setForm(createEmptyForm());
  };

  const handleOpenDialogNovo = () => {
    try {
      pararVozObservacoes();
    } catch {}

    try {
      pararVozConfMapaCargaDesc();
    } catch {}

    try {
      pararVozDifMercadoriaDesc();
    } catch {}

    try {
      pararVozQuebrasDanificadoDesc();
    } catch {}

    try {
      pararVozDifVasilhameDesc();
    } catch {}

    setCampoVozAtivo("");
    setPrefillSemFicha(false);
    setRefTransporteDuplicada(false);
    setIdFichaExistente(0);
    setEstadoFichaExistente(0);
    setEditando(false);
    resetFormNovo();
    setOpen(true);
  };

  const handleOpenDialog = (row) => {
    try {
      pararVozObservacoes();
    } catch {}

    try {
      pararVozConfMapaCargaDesc();
    } catch {}

    try {
      pararVozDifMercadoriaDesc();
    } catch {}

    try {
      pararVozQuebrasDanificadoDesc();
    } catch {}

    try {
      pararVozDifVasilhameDesc();
    } catch {}

    setCampoVozAtivo("");
    setPrefillSemFicha(false);
    setRefTransporteDuplicada(false);
    setIdFichaExistente(0);
    setEstadoFichaExistente(0);
    setEditando(true);
    setForm({
      id: row.id,
      dataficha: row.dataficha?.slice?.(0, 10) || row.dataficha || toInputDate(new Date()),
      reftransporte: row.reftransporte || "",
      motorista: row.motorista || "",
      ajudante: row.ajudante || "",
      matricula: row.matricula || "",
      observacoes: row.observacoes || "",

      estado: row.estado ?? 0,

      conf_mapa_carga: row.conf_mapa_carga ?? 0,
      conf_mapa_carga_desc: row.conf_mapa_carga_desc || "",

      criacao_nome: row.criacao_nome || "",
      conf_mapa_carga_nome: row.conf_mapa_carga_nome || "",
      dif_mercadoria_nome: row.dif_mercadoria_nome || "",
      quebras_danificado_nome: row.quebras_danificado_nome || "",
      dif_vasilhame_nome: row.dif_vasilhame_nome || "",
      encerramento_nome: row.encerramento_nome || "",
      reabertura_nome: row.reabertura_nome || "",

      auxilio_mot_arm1: row.auxilio_mot_arm1 ?? 0,
      auxilio_mot_arm2: row.auxilio_mot_arm2 ?? 0,
      auxilio_ajud_arm1: row.auxilio_ajud_arm1 ?? 0,
      auxilio_ajud_arm2: row.auxilio_ajud_arm2 ?? 0,

      dif_mercadoria: row.dif_mercadoria ?? 0,
      dif_mercadoria_desc: row.dif_mercadoria_desc || "",
      quebras_danificado: row.quebras_danificado ?? 0,
      quebras_danificado_desc: row.quebras_danificado_desc || "",
      dif_vasilhame: row.dif_vasilhame ?? 0,
      dif_vasilhame_desc: row.dif_vasilhame_desc || "",
      limpeza_viatura: row.limpeza_viatura ?? 2,
    });
    setOpen(true);
  };

  const handleClose = () => {
    try {
      pararVozObservacoes();
    } catch {}

    try {
      pararVozConfMapaCargaDesc();
    } catch {}

    try {
      pararVozDifMercadoriaDesc();
    } catch {}

    try {
      pararVozQuebrasDanificadoDesc();
    } catch {}

    try {
      pararVozDifVasilhameDesc();
    } catch {}

    setCampoVozAtivo("");
    setRefTransporteDuplicada(false);
    setIdFichaExistente(0);
    setEstadoFichaExistente(0);
    setOpen(false);
  };

  const abrirFichaExistente = async (id) => {
    const idNum = Number(id || 0);
    if (!idNum) return;

    const rowExistente = rows.find((r) => Number(r.id) === idNum);

    if (rowExistente) {
      handleClose();
      handleOpenDialog(rowExistente);
      return;
    }

    await pesquisar(filtroRef.current);

    setTimeout(() => {
      const rowDepoisPesquisa = rows.find((r) => Number(r.id) === idNum);
      if (rowDepoisPesquisa) {
        handleClose();
        handleOpenDialog(rowDepoisPesquisa);
      } else {
        showAlert(`Não foi possível localizar a ficha existente com ID ${idNum}.`, "error");
      }
    }, 200);
  };

  // =====================================================
  // Save
  // =====================================================
  const validar = () => {
    if (nullIfEmpty(form.dataficha) == null) return "Data da Ficha é obrigatória.";
    if (nullIfEmpty(form.reftransporte) == null) return "Ref. Transporte é obrigatória.";
    if (nullIfEmpty(form.motorista) == null) return "Indicar o(s) Distribuidor(es) é obrigatório.";

    const lenMat = matriculaCleanLength(form.matricula);
    if (lenMat > 0 && lenMat < 6)
      return "A matrícula tem de ter pelo menos 6 caracteres (sem contar hífens).";

    // Mapa de carga tem de ser CORRETA ou INCORRETA
    if (normTri(form.conf_mapa_carga) === 0)
      return "Mapa de Carga é obrigatório (CORRETA ou INCORRETA).";

    if (normTri(form.conf_mapa_carga) === 2 && nullIfEmpty(form.conf_mapa_carga_desc) == null)
      return "Descritivo do Mapa de Carga é obrigatório.";

    if (normTri(form.dif_mercadoria) === 2 && nullIfEmpty(form.dif_mercadoria_desc) == null)
      return "Descritivo de Diferença na Mercadoria é obrigatório.";

    if (normTri(form.quebras_danificado) === 2 && nullIfEmpty(form.quebras_danificado_desc) == null)
      return "Descritivo de Quebras / Produto Danificado é obrigatório.";

    if (normTri(form.dif_vasilhame) === 2 && nullIfEmpty(form.dif_vasilhame_desc) == null)
      return "Descritivo de Diferença no Vasilhame é obrigatório.";

    if (Number(form.id) > 0 && Number(form.limpeza_viatura) === 2)
      return "Limpeza da Viatura é obrigatória.";

    return null;
  };

  const gravarFicha = async () => {
    const parsBase = {
      dataficha: form.dataficha,
      reftransporte: nullIfEmpty(form.reftransporte),

      motorista: nullIfEmpty(form.motorista),
      ajudante: nullIfEmpty(form.ajudante),

      matricula: nullIfEmpty(form.matricula),
      observacoes: nullIfEmpty(form.observacoes),
      estado: Number(form.estado ?? 0),

      auxilio_mot_arm1: norm01(form.auxilio_mot_arm1),
      auxilio_mot_arm2: norm01(form.auxilio_mot_arm2),
      auxilio_ajud_arm1: norm01(form.auxilio_ajud_arm1),
      auxilio_ajud_arm2: norm01(form.auxilio_ajud_arm2),

      conf_mapa_carga: normTri(form.conf_mapa_carga),
      conf_mapa_carga_desc: nullIfEmpty(form.conf_mapa_carga_desc),

      dif_mercadoria: normTri(form.dif_mercadoria),
      dif_mercadoria_desc: nullIfEmpty(form.dif_mercadoria_desc),

      quebras_danificado: normTri(form.quebras_danificado),
      quebras_danificado_desc: nullIfEmpty(form.quebras_danificado_desc),

      dif_vasilhame: normTri(form.dif_vasilhame),
      dif_vasilhame_desc: nullIfEmpty(form.dif_vasilhame_desc),

      limpeza_viatura: Number(form.limpeza_viatura),

      idutilizador,
    };

    const [dados, err, meta] = await handleApi(
      !editando
        ? api.post("Distribuicao/InsFichaTransporte", parsBase)
        : api.post("Distribuicao/UpdFichaTransporte", { ...parsBase, id: form.id })
    );

    if (err) {
      showAlert(err, "error");
      return { ok: false, novoId: 0 };
    }

    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    if (!editando) {
      const newId = Number(dados?.id || 0);

      if (!newId) {
        showAlert("A ficha foi criada mas não recebi o ID. Verifica a resposta da API.", "error");
        return { ok: false, novoId: 0 };
      }

      setForm((s) => ({ ...s, id: newId }));
      setEditando(true);

      await pesquisar(filtroRef.current);
      return { ok: true, novoId: newId };
    }

    await pesquisar(filtroRef.current);
    return { ok: true, novoId: Number(form.id || 0) };
  };

  const handleSave = async () => {
    if (refTransporteDuplicada) {
      showAlert("Já existe uma ficha anterior para esta ref. transporte.", "error");
      return;
    }

    const erro = validar();
    if (erro) {
      showAlert(erro, "error");
      return;
    }

    const resultado = await gravarFicha();
    if (!resultado.ok) return;

    const fichaJaEncerrada = Number(form.estado ?? 0) === 1;

    const semPendentesOcorrencias =
      normTri(form.dif_mercadoria) !== 0 &&
      normTri(form.quebras_danificado) !== 0 &&
      normTri(form.dif_vasilhame) !== 0 &&
      Number(form.limpeza_viatura) !== 2 &&
      (!nullIfEmpty(form.motorista) || Number(form.auxilio_mot_arm1) !== 2) &&
      (!nullIfEmpty(form.motorista) || Number(form.auxilio_mot_arm2) !== 2) &&
      (!nullIfEmpty(form.ajudante) || Number(form.auxilio_ajud_arm1) !== 2) &&
      (!nullIfEmpty(form.ajudante) || Number(form.auxilio_ajud_arm2) !== 2);

    if (!fichaJaEncerrada && semPendentesOcorrencias) {
      setOpenConfirmarEncerramento(true);
      return;
    }

    if (editando) {
      handleClose();
    }
  };

  // =====================================================
  // UI helpers (sem duplicar pessoas)
  // =====================================================

  const abrirTransportesRef = (ref) => {
    const r = (ref ?? "").toString().trim();
    if (!r) return;

    // usa o mesmo route que tens no menu "Transportes"
    const url = `/distribuicao/transportes?reftransporte=${encodeURIComponent(r)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  function formatDateTime(v) {
    if (!v) return "";
    const s = String(v);
    // "2026-02-06T12:34:56Z" ou "...+00"
    if (s.includes("T")) return s.replace("T", " ").slice(0, 19);
    return s;
  }

  function quemQuando(nome, dt) {
    const n = (nome ?? "").toString().trim();
    const d = formatDateTime(dt);
    if (!n && !d) return "—";
    if (n && d) return `${n} em ${d}`;
    return n || d;
  }

  function formatDateOnly(v) {
    if (!v) return "";

    // Se já vier como Date
    if (v instanceof Date) {
      return v.toISOString().slice(0, 10);
    }

    const s = String(v);

    // "2026-02-06T00:00:00"
    if (s.includes("T")) return s.split("T")[0];

    // "06/02/2026 00:00:00" (ou semelhante)
    if (s.includes(" ")) return s.split(" ")[0];

    return s;
  }

  const pessoasMotorista = useMemo(() => {
    const ajud = nullIfEmpty(form.ajudante);
    return pessoas.filter((p) => (p ?? "") !== ajud);
  }, [pessoas, form.ajudante]);

  const pessoasAjudante = useMemo(() => {
    const mot = nullIfEmpty(form.motorista);
    return pessoas.filter((p) => (p ?? "") !== mot);
  }, [pessoas, form.motorista]);

  const podeEditarAux = podeAlterarAuxilio(form.dataficha, isAdminLike, isArmazem);

  const dialogEdicaoCompleta = !editando || podeCriar;
  const podeEditarCamposBase = dialogEdicaoCompleta;

  const temId = Number(form.id) > 0;

  const temPendentesOcorrencias =
    normTri(form.dif_mercadoria) === 0 ||
    normTri(form.quebras_danificado) === 0 ||
    normTri(form.dif_vasilhame) === 0;

  const handleEncerrar = async () => {
    if (!temId) return;

    if (temPendentesOcorrencias) {
      showAlert("Não é possível encerrar: existem ocorrências em PENDENTE.", "error");
      return;
    }

    if (Number(form.limpeza_viatura) === 2) {
      showAlert("Não é possível encerrar: tem de indicar se a viatura estava limpa.", "error");
      return;
    }

    if (nullIfEmpty(form.motorista) && Number(form.auxilio_mot_arm1) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 1 auxiliou no Armazém 1.",
        "error"
      );
      return;
    }

    if (nullIfEmpty(form.motorista) && Number(form.auxilio_mot_arm2) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 1 auxiliou no Armazém 2.",
        "error"
      );
      return;
    }

    if (nullIfEmpty(form.ajudante) && Number(form.auxilio_ajud_arm1) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 2 auxiliou no Armazém 1.",
        "error"
      );
      return;
    }

    if (nullIfEmpty(form.ajudante) && Number(form.auxilio_ajud_arm2) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 2 auxiliou no Armazém 2.",
        "error"
      );
      return;
    }

    const parsBase = {
      dataficha: form.dataficha,
      reftransporte: nullIfEmpty(form.reftransporte),

      motorista: nullIfEmpty(form.motorista),
      ajudante: nullIfEmpty(form.ajudante),

      matricula: nullIfEmpty(form.matricula),
      observacoes: nullIfEmpty(form.observacoes),

      auxilio_mot_arm1: norm01(form.auxilio_mot_arm1),
      auxilio_mot_arm2: norm01(form.auxilio_mot_arm2),
      auxilio_ajud_arm1: norm01(form.auxilio_ajud_arm1),
      auxilio_ajud_arm2: norm01(form.auxilio_ajud_arm2),

      conf_mapa_carga: normTri(form.conf_mapa_carga),
      conf_mapa_carga_desc: nullIfEmpty(form.conf_mapa_carga_desc),

      dif_mercadoria: normTri(form.dif_mercadoria),
      dif_mercadoria_desc: nullIfEmpty(form.dif_mercadoria_desc),

      quebras_danificado: normTri(form.quebras_danificado),
      quebras_danificado_desc: nullIfEmpty(form.quebras_danificado_desc),

      dif_vasilhame: normTri(form.dif_vasilhame),
      dif_vasilhame_desc: nullIfEmpty(form.dif_vasilhame_desc),

      limpeza_viatura: Number(form.limpeza_viatura),

      estado: 1,
      idutilizador,
    };

    const [_, err, meta] = await handleApi(
      api.post("Distribuicao/UpdFichaTransporte", { ...parsBase, id: form.id })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    setForm((s) => ({ ...s, estado: 1 }));
    handleClose();
    await pesquisar(filtroRef.current);
  };

  const handleToggleEstadoLinha = async (ficha) => {
    if (!ficha?.id) return;

    const fichaEncerrada = Number(ficha.estado ?? 0) === 1;

    if (fichaEncerrada && !isAdminLike) {
      showAlert("Só utilizadores AdminLike podem reabrir fichas encerradas.", "error");
      return;
    }

    const temPendentesLinha =
      normTri(ficha.dif_mercadoria) === 0 ||
      normTri(ficha.quebras_danificado) === 0 ||
      normTri(ficha.dif_vasilhame) === 0;

    if (!fichaEncerrada && temPendentesLinha) {
      showAlert("Não é possível encerrar: existem ocorrências em PENDENTE.", "error");
      return;
    }

    if (!fichaEncerrada && Number(ficha.limpeza_viatura) === 2) {
      showAlert("Não é possível encerrar: tem de indicar se a viatura estava limpa.", "error");
      return;
    }

    if (!fichaEncerrada && nullIfEmpty(ficha.motorista) && Number(ficha.auxilio_mot_arm1) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 1 auxiliou no Armazém 1.",
        "error"
      );
      return;
    }

    if (!fichaEncerrada && nullIfEmpty(ficha.motorista) && Number(ficha.auxilio_mot_arm2) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 1 auxiliou no Armazém 2.",
        "error"
      );
      return;
    }

    if (!fichaEncerrada && nullIfEmpty(ficha.ajudante) && Number(ficha.auxilio_ajud_arm1) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 2 auxiliou no Armazém 1.",
        "error"
      );
      return;
    }

    if (!fichaEncerrada && nullIfEmpty(ficha.ajudante) && Number(ficha.auxilio_ajud_arm2) === 2) {
      showAlert(
        "Não é possível encerrar: tem de indicar se o distribuidor 2 auxiliou no Armazém 2.",
        "error"
      );
      return;
    }

    const parsBase = {
      dataficha: formatDateOnly(ficha.dataficha),
      reftransporte: nullIfEmpty(ficha.reftransporte),

      motorista: nullIfEmpty(ficha.motorista),
      ajudante: nullIfEmpty(ficha.ajudante),

      matricula: nullIfEmpty(ficha.matricula),
      observacoes: nullIfEmpty(ficha.observacoes),

      auxilio_mot_arm1: norm01(ficha.auxilio_mot_arm1),
      auxilio_mot_arm2: norm01(ficha.auxilio_mot_arm2),
      auxilio_ajud_arm1: norm01(ficha.auxilio_ajud_arm1),
      auxilio_ajud_arm2: norm01(ficha.auxilio_ajud_arm2),

      conf_mapa_carga: normTri(ficha.conf_mapa_carga),
      conf_mapa_carga_desc: nullIfEmpty(ficha.conf_mapa_carga_desc),

      dif_mercadoria: normTri(ficha.dif_mercadoria),
      dif_mercadoria_desc: nullIfEmpty(ficha.dif_mercadoria_desc),

      quebras_danificado: normTri(ficha.quebras_danificado),
      quebras_danificado_desc: nullIfEmpty(ficha.quebras_danificado_desc),

      dif_vasilhame: normTri(ficha.dif_vasilhame),
      dif_vasilhame_desc: nullIfEmpty(ficha.dif_vasilhame_desc),

      limpeza_viatura: Number(ficha.limpeza_viatura),

      estado: fichaEncerrada ? 0 : 1,
      idutilizador,
    };

    const [_, err, meta] = await handleApi(
      api.post("Distribuicao/UpdFichaTransporte", { ...parsBase, id: ficha.id })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) showAlert(meta.message, meta?.severity || "success");

    await pesquisar(filtroRef.current);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          {/* Linha de filtros (layout JFB) */}
          <MDBox p={2} display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data inicial
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={filtro.dataIni}
                onChange={(e) => setFiltro((s) => ({ ...s, dataIni: e.target.value }))}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data final
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={filtro.dataFim}
                onChange={(e) => setFiltro((s) => ({ ...s, dataFim: e.target.value }))}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 180 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Transporte (ref)
              </MDTypography>
              <TextField
                size="small"
                value={filtro.reftransporte}
                onChange={(e) => setFiltro((s) => ({ ...s, reftransporte: e.target.value }))}
                onBlur={(e) =>
                  setFiltro((s) => ({
                    ...s,
                    reftransporte: formatRefTransporteOnBlur(e.target.value),
                  }))
                }
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Matrícula
              </MDTypography>
              <Autocomplete
                size="small"
                options={matriculas || []}
                value={filtro.matricula || null}
                onChange={(_, v) => setFiltro((s) => ({ ...s, matricula: v || "" }))}
                getOptionLabel={(opt) => opt || ""}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODAS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 260 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Distribuidor
              </MDTypography>
              <Autocomplete
                freeSolo
                size="small"
                options={pessoas}
                value={filtro.pessoa || null}
                onChange={(_, v) => setFiltro((s) => ({ ...s, pessoa: v || "" }))}
                getOptionLabel={(opt) => opt || ""}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODOS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            <MDBox display="flex" alignItems="flex-end" gap={1} flexWrap="wrap">
              <MDButton
                color="info"
                onClick={() => pesquisar(filtroRef.current)}
                disabled={loading}
              >
                ATUALIZAR ANÁLISE
              </MDButton>

              {podeCriar && (
                <MDButton color="success" onClick={handleOpenDialogNovo}>
                  NOVA FICHA
                </MDButton>
              )}

              {podeCriar && (
                <MDButton color="dark" onClick={handleOpenSemFicha} disabled={loading}>
                  VER TRANSPORTES SEM FICHA
                </MDButton>
              )}
            </MDBox>
          </MDBox>

          {/* Registos */}
          <MDBox px={2} pb={1}>
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Registos: {rows.length}
            </MDTypography>
          </MDBox>

          {/* Tabela */}
          <MDBox
            px={2}
            pb={2}
            style={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "60vh",
            }}
          >
            <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {headerGroups.map((hg) => (
                  <tr key={hg.id} {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th
                        key={col.id}
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        style={{
                          textAlign: "left",
                          padding: "10px 10px 6px 10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          verticalAlign: "bottom",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.75}>
                          <span>{col.render("Header")}</span>
                          <span style={{ fontSize: 11, opacity: 0.7 }}>
                            {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                          </span>
                        </MDBox>

                        <MDBox mt={0.5}>{col.canFilter ? col.render("Filter") : null}</MDBox>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {tableRows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr key={row.id} {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={openSemFicha}
        onClose={handleCloseSemFicha}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-transportes-sem-ficha-title" sx={{ cursor: "move" }}>
          Transportes sem ficha
        </DialogTitle>

        <DialogContent dividers>
          {loadingSemFicha && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          {!loadingSemFicha && (
            <>
              <MDBox mb={1}>
                <MDTypography variant="button" sx={{ fontSize: 12 }}>
                  Registos: {rowsSemFicha.length}
                </MDTypography>
              </MDBox>

              <MDBox
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "50vh",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        Ações
                      </th>

                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        Data
                      </th>

                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        Ref. Transp.
                      </th>

                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        Distribuidor 1
                      </th>

                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        Distribuidor 2
                      </th>

                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 2,
                        }}
                      >
                        Matrícula
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rowsSemFicha.map((row, idx) => (
                      <tr key={`${row?.reftransporte || ""}_${idx}`}>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          <MDButton
                            color="success"
                            size="small"
                            onClick={() => handleCriarFichaDeTransporte(row)}
                          >
                            Criar Ficha
                          </MDButton>
                        </td>

                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          {formatDateOnly(row?.dataficha)}
                        </td>

                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                            fontWeight: 700,
                            color: "#1a73e8",
                          }}
                        >
                          {row?.reftransporte || ""}
                        </td>

                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          {row?.motorista || ""}
                        </td>

                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          {row?.ajudante || ""}
                        </td>

                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            verticalAlign: "middle",
                          }}
                        >
                          {row?.matricula || ""}
                        </td>
                      </tr>
                    ))}

                    {rowsSemFicha.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            fontSize: 13,
                            textAlign: "center",
                            opacity: 0.75,
                          }}
                        >
                          Não existem transportes sem ficha para o período indicado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseSemFicha}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* DIALOG */}
      <DialogFichaTransporteForm
        open={open}
        onClose={handleClose}
        editando={editando}
        form={form}
        setForm={setForm}
        podeEditarCamposBase={podeEditarCamposBase}
        matriculas={matriculas}
        pessoasMotorista={pessoasMotorista}
        pessoasAjudante={pessoasAjudante}
        nullIfEmpty={nullIfEmpty}
        normTri={normTri}
        formatRefTransporteOnBlur={formatRefTransporteOnBlur}
        formatMatriculaOnBlur={formatMatriculaOnBlur}
        matriculaCleanLength={matriculaCleanLength}
        prefillSemFicha={prefillSemFicha}
        setPrefillSemFicha={setPrefillSemFicha}
        carregarUltimaEquipaPorMatricula={carregarUltimaEquipaPorMatricula}
        verificarFichaTransportePorRef={verificarFichaTransportePorRef}
        refTransporteDuplicada={refTransporteDuplicada}
        idFichaExistente={idFichaExistente}
        estadoFichaExistente={estadoFichaExistente}
        showAlert={showAlert}
        suporteVoz={suporteVoz}
        campoVozAtivo={campoVozAtivo}
        setCampoVozAtivo={setCampoVozAtivo}
        botaoVozDesativado={botaoVozDesativado}
        reconhecendoVozConfMapaCargaDesc={reconhecendoVozConfMapaCargaDesc}
        pararVozConfMapaCargaDesc={pararVozConfMapaCargaDesc}
        iniciarVozConfMapaCargaDesc={iniciarVozConfMapaCargaDesc}
        reconhecendoVozObservacoes={reconhecendoVozObservacoes}
        pararVozObservacoes={pararVozObservacoes}
        iniciarVozObservacoes={iniciarVozObservacoes}
        reconhecendoVozDifMercadoriaDesc={reconhecendoVozDifMercadoriaDesc}
        pararVozDifMercadoriaDesc={pararVozDifMercadoriaDesc}
        iniciarVozDifMercadoriaDesc={iniciarVozDifMercadoriaDesc}
        reconhecendoVozQuebrasDanificadoDesc={reconhecendoVozQuebrasDanificadoDesc}
        pararVozQuebrasDanificadoDesc={pararVozQuebrasDanificadoDesc}
        iniciarVozQuebrasDanificadoDesc={iniciarVozQuebrasDanificadoDesc}
        reconhecendoVozDifVasilhameDesc={reconhecendoVozDifVasilhameDesc}
        pararVozDifVasilhameDesc={pararVozDifVasilhameDesc}
        iniciarVozDifVasilhameDesc={iniciarVozDifVasilhameDesc}
        temId={temId}
        podeEditarAux={podeEditarAux}
        temPendentesOcorrencias={temPendentesOcorrencias}
        handleEncerrar={handleEncerrar}
        handleSave={handleSave}
        abrirFichaExistente={abrirFichaExistente}
        setOpenFicheiros={setOpenFicheiros}
      />

      <DialogFichaTransporteFicheiros
        open={openFicheiros}
        onClose={handleCloseFicheiros}
        idFichaTransporte={Number(fichaFicheiros?.id || form?.id || 0)}
      />

      <Dialog
        open={openConfirmarEncerramento}
        onClose={() => setOpenConfirmarEncerramento(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Encerrar ficha</DialogTitle>
        <DialogContent>
          A ficha foi gravada e já não tem ocorrências em <strong>PENDENTE</strong>.
          <br />
          <br />
          Pretende encerrar já a ficha de transporte <strong>{form.reftransporte}</strong>?
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => {
              setOpenConfirmarEncerramento(false);
              handleClose();
            }}
          >
            Não
          </MDButton>

          <MDButton
            color="success"
            onClick={async () => {
              setOpenConfirmarEncerramento(false);
              await handleEncerrar();
            }}
          >
            Sim, encerrar
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogFichaTransporteDetalhe
        open={openInfo}
        onClose={handleCloseInfo}
        row={infoRow}
        onAbrirTransportesRef={abrirTransportesRef}
      />
    </DashboardLayout>
  );
}
